import React, { Fragment, useEffect, useState } from 'react';

// Material UI
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardActions from '@mui/material/CardActions';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningIcon from '@mui/icons-material/Warning';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CommentIcon from '@mui/icons-material/Comment';

// Components
import useStyles from './styles';
import SelectButton from './SelectButton';

// Hooks
import useCard from '../../../../../hooks/orders/useCard';
import useUser from '../../../../../hooks/user/useUser';

// Varios
import { utils } from '../../../../../utils/utils';
import { displayMethods, paymentMethods } from '../../../../../models/PaymentMethods';
import { ShippingTypes } from '../../../../../models/ShippingTypes';
import { PaymentStates } from '../../../../../models/PaymentStates';
import { InvoiceTypeFormat } from '../../../../../models/EnumEntity';
import useCart from '../../../../../hooks/orders/CartProvider';

const PaymentCardView = ({ orderId, binNumber, installmentId, bankId, cardId, amount }) => {
    const { searchCardPlansWithAnAmount } = useCard(orderId);

    const [card, setCard] = useState()
    const [installment, setInstallment] = useState()

    useEffect(() => {
        searchCardPlansWithAnAmount(bankId, cardId, binNumber, amount)
            .then(card => {
                setCard(card);
                if (card.installments.length > 0)
                    setInstallment(card.installments.find(i => i.id === installmentId))
            });
    }, [searchCardPlansWithAnAmount, bankId, cardId, installmentId, binNumber, amount])

    if (!card)
        return null;

    return (
        <>
            <Typography variant='overline' display='block'>
                BANCO <strong>{card.bank.name}</strong>
            </Typography>
            <Typography variant='overline' display='block'>
                {
                    installment
                        ? <>TARJETA <strong>{card.brand}</strong> {`(${binNumber}xx)`} en <strong>{installment.displayText}</strong></>
                        : <>TARJETA <strong>{card.brand}</strong> {`(${binNumber}xx)`}</>
                }
            </Typography>
        </>
    )
}

const SelectPanel = ({ onSellerClick, onCustomerClick, onPaymentClick, onPromotionClick, onShippingDataClick, onInvoiveClick, onObservationClick }) => {
    const { classes } = useStyles();
    const {
        warehouses,
        clearShippingData,
        sale: {
            orderId, items, payments, amount, totalPayedAmount, shippingData, seller,
            customer, invoiceType, discount, refund, observations, itemsToReturn, originalOrderId
        },
        existProcessedPayment
    } = useCart();
    const { branch } = useUser();

    const isPaymentAmountValid = () => {
        return (payments?.length === 0) ||
            (payments?.filter(p => p.state === PaymentStates.Initialized)
                ?.reduce((acum, curr) => acum + curr.totalAmount, 0).toFixed(2) === (amount - totalPayedAmount).toFixed(2));
    }

    const PromotionCardView = () => {
        const promotions = utils.selectMany(items, 'promotions');
        const avaliables = utils.selectMany(promotions, 'avaliables');
        const gb = utils.groupBy(avaliables, 'promotionId');
        const count = Object.keys(gb).length;

        if (count === 1)
            return (
                <Typography variant='overline' display='block'>
                    <strong>1</strong> promocion aplicada
                </Typography>
            )

        return (
            <Typography variant='overline' display='block'>
                <strong>{Object.keys(gb).length}</strong> promociones aplicadas
            </Typography>
        )
    }

    const ShippingDataView = () => {
        const getWarehouseName = () => {
            const warehouse = warehouses.find(warehouse =>
                warehouse.id === shippingData.warehouseIdDestination);
            if (items.length <= 0) {
                clearShippingData()
                return '';
            } else {
                return warehouse?.name;
            }
        }

        if (shippingData.type === ShippingTypes.Pickup)
            return (
                <Typography variant='overline' display='block'>
                    <strong>Retiro</strong> por <strong>{getWarehouseName()}</strong>
                </Typography>
            )

        return (
            <>
                <Typography variant='overline' display='block'>
                    <strong>Envio a domicilio</strong>
                </Typography>
                <Typography variant='overline' display='block' noWrap>
                    {shippingData.address.fullAddress}
                </Typography>
            </>
        )
    }

    const ValidateShipping = () => 
        !shippingData && items?.some(i => i.warehouseId !== branch?.warehouseId);

    return (
        <Card>
            <CardActionArea>
                <CardActions
                    onClick={onSellerClick}
                    className={classes.cardArea}
                >
                    <SelectButton
                        isSelected={!!seller?.id}
                        notSelctedMessage='Cargar vendedor.'
                        icon={AssignmentIndIcon}
                    >
                        <Typography variant='overline' display='block'>
                            <strong>Vendedor {seller?.code}</strong>
                        </Typography>
                        <Typography variant='overline' display='block'>
                            {seller?.userName}
                        </Typography>
                    </SelectButton>
                </CardActions>
            </CardActionArea>
            <Divider variant='middle' />
            <CardActionArea
                disabled={existProcessedPayment}
                className={existProcessedPayment && classes.cardAreaDisabled}
            >
                <CardActions
                    onClick={onCustomerClick}
                    className={classes.cardArea}
                >
                    <SelectButton
                        isSelected={!!customer?.id}
                        notSelctedMessage='Cargar cliente.'
                        icon={PersonIcon}
                    >
                        <Typography variant='overline' display='block'>
                            <strong>{customer?.firstName} {customer?.lastName}</strong>
                        </Typography>
                        <Typography variant='overline' display='block'>
                            {customer?.documentType} {customer?.documentNumber}
                        </Typography>
                    </SelectButton>
                </CardActions>
            </CardActionArea>
            <Divider variant='middle' />
            <CardActionArea
                disabled={existProcessedPayment}
                className={existProcessedPayment && classes.cardAreaDisabled}
            >
                <CardActions
                    onClick={onInvoiveClick}
                    className={classes.cardArea}
                >
                    <SelectButton
                        isSelected={!!invoiceType}
                        notSelctedMessage='Cliente no seleccionado'
                        icon={ReceiptIcon}
                    >
                        <Typography variant='overline' display='block'>
                            <strong>{InvoiceTypeFormat[invoiceType]}</strong>
                        </Typography>
                    </SelectButton>
                </CardActions>
            </CardActionArea>
            <Divider variant='middle' />
            <CardActionArea
                disabled={existProcessedPayment}
                className={existProcessedPayment && classes.cardAreaDisabled}
            >
                <CardActions
                    onClick={onShippingDataClick}
                    className={classes.cardArea}
                >
                    <SelectButton
                        isSelected={!!shippingData}
                        notSelctedMessage={ValidateShipping() ? 'Se agrego producto de otro local' : 'Modo de envio no seleccionado.'}
                        icon={ValidateShipping() ? WarningIcon : LocalShippingIcon}
                    >
                        <ShippingDataView />
                    </SelectButton>
                </CardActions>
            </CardActionArea>
            <Divider variant='middle' />
            <CardActionArea>
                <CardActions
                    onClick={totalPayedAmount < amount ? onPaymentClick : null}
                    className={(payments?.filter(p => p.state === PaymentStates.Initialized)?.length > 1)
                        ? classes.cardAreaHigh
: classes.cardArea}
                >
                    <SelectButton
                        isSelected={payments.some(p => p.state === PaymentStates.Initialized)}
                        notSelctedMessage='Cargar medio de pago.'
                        icon={!isPaymentAmountValid() ? WarningIcon : PaymentIcon}
                    >
                        {
                            !isPaymentAmountValid() &&
                            <Typography variant='overline' display='block'>
                                <strong>Revisar medios de pago</strong>
                            </Typography>
                        }
                        {isPaymentAmountValid() &&
                            payments
                                ?.filter(p => p.state === PaymentStates.Initialized)
                                .map((payment, index) =>
                                    <Fragment key={index}>
                                        <Typography variant='overline' display='block'>
                                            Medio de pago <strong>{displayMethods[payment.paymentMethodType]}</strong>
                                        </Typography>
                                        {
                                            (payment.paymentMethodType === paymentMethods.Card ||
                                                payment.paymentMethodType === paymentMethods.Posnet
                                            ) &&
                                            <PaymentCardView
                                                orderId={orderId}
                                                {...payment}
                                            />
                                        }
                                        {payments?.filter(p => p.state === PaymentStates.Initialized).length - 1 !== index &&
                                            <Divider />
                                        }
                                    </Fragment>
                                )
                        }
                    </SelectButton>
                </CardActions>
            </CardActionArea>
            <Divider variant='middle' />
            <CardActionArea>
                <CardActions
                    onClick={onPromotionClick}
                    className={classes.cardArea}
                >
                    <SelectButton
                        isSelected={!!discount || !!refund}
                        notSelctedMessage='Sin promociones'
                        icon={LocalOfferIcon}
                    >
                        <PromotionCardView />
                    </SelectButton>
                </CardActions>
            </CardActionArea>
            <Divider variant='middle' />
            <CardActionArea>
                <CardActions
                    onClick={onObservationClick}
                    className={classes.cardArea}
                >
                    <SelectButton
                        isSelected={!!observations}
                        notSelctedMessage={itemsToReturn?.length > 0 && !originalOrderId ? 'La observacion es obligatoria' : 'Sin observaciones'}
                        icon={itemsToReturn?.length > 0 && !observations && !originalOrderId ? WarningIcon : CommentIcon}
                    >
                        <Tooltip title={observations}>
                            <Typography variant='overline' display='block' noWrap className={classes.observationContent}>
                                {observations}
                            </Typography>
                        </Tooltip>
                    </SelectButton>
                </CardActions>
            </CardActionArea>
        </Card>
    )
}

export default SelectPanel;